import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useSearchMutaion from './../../../../hooks/apis/useSearchMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { SAM_BASIC_02_Schema } from './SAM_BASIC_02_Schema';

/** GetSamBasic02R5 단일 페이로드 여부 (Mst / Dailies / DailiesByPmCd / CenterSigns) */
function IsGetSamBasic02R5Payload(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return (
    obj.mst != null ||
    obj.Mst != null ||
    Array.isArray(obj.dailies) ||
    Array.isArray(obj.Dailies) ||
    Array.isArray(obj.dailiesByPmCd) ||
    Array.isArray(obj.DailiesByPmCd) ||
    Array.isArray(obj.centerSigns) ||
    Array.isArray(obj.CenterSigns)
  );
}

/**
 * GetSamBasic02R5 응답 정규화
 * - Data: [{ Mst, CenterSigns, Dailies(요청 WorkDate), DailiesByPmCd(PM 전체) }]
 * - handleQuerySuccess는 data/Data 배열만 행으로 넘기므로 1건 배열로 통일
 */
function NormalizeGetSamBasic02R5ApiResponse(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  const isSuccess = raw.isSuccess ?? raw.IsSuccess;
  if (isSuccess === false) {
    const err = new Error(
      raw.message ?? raw.Message ?? raw.errorMsg ?? raw.ErrorMsg ?? '조회에 실패했습니다.'
    );
    err.responseData = raw;
    throw err;
  }

  let rows = null;
  if (Array.isArray(raw.data)) rows = raw.data;
  else if (Array.isArray(raw.Data)) rows = raw.Data;

  if (!rows) {
    const nested = raw.data ?? raw.Data;
    if (IsGetSamBasic02R5Payload(nested)) {
      rows = [nested];
    } else if (IsGetSamBasic02R5Payload(raw)) {
      rows = [raw];
    } else {
      rows = [];
    }
  } else if (rows.length === 1 && IsGetSamBasic02R5Payload(rows[0])) {
    // already ok
  } else {
    rows = rows.filter((item) => IsGetSamBasic02R5Payload(item));
  }

  return {
    ...raw,
    isSuccess: isSuccess ?? true,
    IsSuccess: isSuccess ?? true,
    data: rows,
    Data: rows,
  };
}

function AssertApiSuccess(result, fallbackMessage) {
  const ok = result?.isSuccess ?? result?.IsSuccess;
  if (!ok) {
    const err = new Error(
      result?.message ??
        result?.Message ??
        result?.errorMsg ??
        result?.ErrorMsg ??
        fallbackMessage
    );
    err.responseData = result;
    throw err;
  }
  return result;
}

export const useSearch1 = (options = {}) => {
  const { onSuccess, context } = options;

  const query = useQuery({
    queryKey: [
      'SafetyWorkManagement',
      'getSamBasic02R4',
      context?.search?.custCd || '',
      context?.search?.depCd || '',
      context?.search?.workStartDate || context?.search?.startDate || '',
      context?.search?.workEndDate || context?.search?.endDate || '',
      context?.search?.gubun || context?.search?.status || '',
      context?.userInfo?.userid || context?.userInfo?.userId || '',
    ],
    queryFn: async () => {
      const params = mapParamsBySchema(
        SAM_BASIC_02_Schema.mapper.search1,
        context
      );
      const response = await apiRequest(
        'post',
        Global.urls.SafetyWorkManagement.getSamBasic02R4,
        params
      );
      return response;
    },
    enabled: false,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * 작업허가서 상세 조회 — SamBasicController.GetSamBasic02R5
 * [HttpPost] [FromQuery] PmCd, WorkDate, UserId (AES) · 본문 없음
 */
export const useSearch2 = (successOptions) => {
  return useSearchMutaion(
    async (context) => {
      const queryParams = mapParamsBySchema(
        SAM_BASIC_02_Schema.mapper.search2,
        context
      );
      const url = `${Global.base.useUrl}${Global.urls.SafetyWorkManagement.getSamBasic02R5}`;
      const { data } = await Commons.Axios('post', url, queryParams, {});
      return NormalizeGetSamBasic02R5ApiResponse(data);
    },
    successOptions,
    false
  );
};

// 안전작업허가서 반려이력 조회
export const useSearch3 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      SAM_BASIC_02_Schema.mapper.search3,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SafetyWorkManagement.getSamBasic02R3,
      params
    );
  }, successOptions);
};

// 안전관리자 조회 (GetSafetyManager)
export const useSearch4 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      SAM_BASIC_02_Schema.mapper.search4,
      context
    );
    return apiRequest('post', Global.urls.Utility.getSafetyManager, params);
  }, successOptions);
};

/**
 * GetSamBasic02R2Catalog — [HttpPost] [FromQuery] custCd (AES)
 * WorkTypes + RiskCodes + EquipCodes + PreparationCodes (레포트 전체 슬롯용)
 */
export const useSearch5 = (successOptions) => {
  return useSearchMutaion(async (context) => {
    const queryParams = mapParamsBySchema(
      SAM_BASIC_02_Schema.mapper.search5,
      context
    );
    const url = `${Global.base.useUrl}${Global.urls.SafetyWorkManagement.getSamBasic02R2Catalog}`;
    const { data } = await Commons.Axios('post', url, queryParams, {});
    return data;
  }, successOptions);
};

/**
 * 작업허가 반려/중간결재/최종결재 (SamBasic SaveSamBasic02U2) — [FromBody] List
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows, context = {}) => {
    const payloads = mapRowsBySchema(
      rows,
      SAM_BASIC_02_Schema.mapper.saveSamBasic02U2,
      context
    );

    if (!payloads?.length) {
      return { isSuccess: true, IsSuccess: true };
    }

    const result = await apiRequest(
      'post',
      Global.urls.SafetyWorkManagement.saveSamBasic02U2,
      payloads
    );
    return AssertApiSuccess(result, '저장에 실패했습니다.');
  }, options);
};

/**
 * 작업허가 연장 승인/반려 (SamBasic SaveSamBasic02U6)
 */
export const useSave3 = (options = {}) =>
  useSaveMutation(async (rows, context = {}) => {
    const payloads = mapRowsBySchema(
      rows,
      SAM_BASIC_02_Schema.mapper.save3,
      context
    );
    const payload = payloads?.[0] ?? {};
    const result = await apiRequest(
      'post',
      Global.urls.SafetyWorkManagement.saveSamBasic02U6,
      payload
    );
    return AssertApiSuccess(result, '연장 처리에 실패했습니다.');
  }, options);

/**
 * DeleteSamBasic02D1 — [HttpPost] [FromBody] List { PmCd (AES) }
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payloads = mapRowsBySchema(rows, SAM_BASIC_02_Schema.mapper.delete1);
    const result = await apiRequest(
      'post',
      Global.urls.SafetyWorkManagement.deleteSamBasic02D1,
      payloads
    );
    return AssertApiSuccess(result, '삭제에 실패했습니다.');
  }, options);
};

/**
 * DeleteSamBasic02D2 — [HttpPost] [FromBody] { DailyCd (AES), DtlCd? (AES, 일자별 삭제 시 생략) }
 */
export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payloads = mapRowsBySchema(rows, SAM_BASIC_02_Schema.mapper.delete2);

    let lastResult;
    for (const body of payloads) {
      lastResult = await apiRequest(
        'post',
        Global.urls.SafetyWorkManagement.deleteSamBasic02D2,
        body
      );
      AssertApiSuccess(lastResult, '상세 삭제에 실패했습니다.');
    }
    return lastResult;
  }, options);
};
